import { useState } from 'react'

import { useToast } from '@/components/Toast'
import { CTAButton } from '@/components/ui/CTAButton'
import { ProgressBar } from '@/components/ui/ProgressBar'
import { BlockStockAlertModal, PaymentSummaryCard, ProductCard } from '@/pages/SealedVinylPage/components'
import type { BlockDialogKind } from '@/pages/SealedVinylPage/hooks/useProceedToPayment'
import { useProceedToPayment } from '@/pages/SealedVinylPage/hooks/useProceedToPayment'
import { SEALED_VINYL_DEFAULT_QUANTITY } from '@/pages/SealedVinylPage/lib/constants'
import { calcSealedVinylAmounts } from '@/pages/SealedVinylPage/lib/utils'

import { Notice } from './components'
import {
  OrderBottomBar,
  OrderContainer,
  OrderHeading,
  OrderHeadingSection,
  OrderProductSection,
  OrderScrollArea,
  OrderSubheading,
  OrderSummarySection,
  ProgressBarSection,
} from './styles'

interface OrderStepProps {
  /** 신청 화면에서 선택한 수량을 결제 step 으로 전달 */
  onProceed: (quantity: number) => void
  /** 초기 수량 — 결제 step 에서 '이전'으로 돌아오거나 카드 등록 복귀 시 직전 선택 수량 유지용. 미지정 시 기본값. */
  initialQuantity?: number
}

export function OrderStep({ onProceed, initialQuantity }: OrderStepProps) {
  const [quantity, setQuantity] = useState(initialQuantity ?? SEALED_VINYL_DEFAULT_QUANTITY)
  const [blockDialog, setBlockDialog] = useState<BlockDialogKind | null>(null)
  const { isChecking, proceed } = useProceedToPayment()
  const { show: showToast } = useToast()

  const { itemAmount, deliveryFee, totalAmount } = calcSealedVinylAmounts(quantity)

  const handleSubmit = async () => {
    const result = await proceed(quantity)
    if (result.ok) {
      onProceed(quantity)
      return
    }
    if (result.kind === 'soldOut') {
      setBlockDialog('soldOut')
      return
    }
    if (result.kind === 'notEnoughStock') {
      setBlockDialog('notEnoughStock')
      return
    }
    showToast(result.message)
  }

  return (
    <>
      <OrderContainer>
        <ProgressBarSection>
          <ProgressBar
            current={1}
            total={2}
          />
        </ProgressBarSection>
        <OrderScrollArea>
          <OrderHeadingSection>
            <OrderHeading>밀봉비닐을 무료로 받아보세요</OrderHeading>
            <OrderSubheading>수거 신청 시 사용할 수 있는 밀봉비닐을 드려요</OrderSubheading>
          </OrderHeadingSection>
          <OrderProductSection>
            <ProductCard
              quantity={quantity}
              onQuantityChange={setQuantity}
            />
          </OrderProductSection>
          <OrderSummarySection>
            <PaymentSummaryCard
              itemAmount={itemAmount}
              deliveryFee={deliveryFee}
              totalAmount={totalAmount}
            />
          </OrderSummarySection>
          <Notice />
        </OrderScrollArea>
        <OrderBottomBar>
          <CTAButton
            loading={isChecking}
            onClick={handleSubmit}
          >
            신청하기
          </CTAButton>
        </OrderBottomBar>
      </OrderContainer>
      {blockDialog && (
        <BlockStockAlertModal
          kind={blockDialog}
          onClose={() => setBlockDialog(null)}
        />
      )}
    </>
  )
}
